import { type Address } from "@modules/address/addressTypes"
import { type Chrono } from "@modules/chrono/chronoTypes"
import { convertChronoFromDto } from "@modules/chrono/services/chronoDtos"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import {
  type ExploreBenchmarkItemDto,
  type ExploreBoxItemDto,
  type ExploreContentDto,
  type ExploreProfileItemDto,
  type ExploreProgramBundleItemDto,
  type ExploreProgramItemDto,
  type ExploreProProgramItemDto,
} from "@modules/explore/gateway/exploreDto"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { type HUImage } from "@modules/types/imageTypes"

export class ExploreContentEntity {
  id: string
  type: ExploreFilter
  benchmark?: ExploreBenchmarkItemEntity
  program?: ExploreProgramItemEntity | ExploreProgramBundleItemEntity | ExploreProProgramItemEntity
  box?: ExploreBoxItemEntity
  profile?: ExploreProfileItemEntity

  constructor(globalExploreContentEntity: ExploreContentEntity) {
    this.id = globalExploreContentEntity.id
    this.type = globalExploreContentEntity.type
    this.benchmark = globalExploreContentEntity.benchmark
    this.program = globalExploreContentEntity.program
    this.box = globalExploreContentEntity.box
    this.profile = globalExploreContentEntity.profile
  }

  static fromDto(dto: ExploreContentDto): ExploreContentEntity {
    switch (dto.contentType) {
      case ExploreFilter.PROGRAM:
        return new ExploreProgramContentEntity({
          id: dto.content.id,
          type: ExploreFilter.PROGRAM,
          program: ExploreProgramItemEntity.fromDto(dto.content),
        })
      case ExploreFilter.PROGRAM_BUNDLE:
        return new ExploreProgramBundleContentEntity({
          id: dto.content.id,
          type: ExploreFilter.PROGRAM_BUNDLE,
          program: ExploreProgramBundleItemEntity.fromDto(dto.content),
        })
      case ExploreFilter.PROGRAM_PRO:
        return new ExploreProProgramContentEntity({
          id: dto.content.id,
          type: ExploreFilter.PROGRAM_PRO,
          program: ExploreProProgramItemEntity.fromDto(dto.content),
        })
      case ExploreFilter.BENCHMARK:
        return new ExploreBenchmarkContentEntity({
          id: dto.content.id,
          type: ExploreFilter.BENCHMARK,
          benchmark: ExploreBenchmarkItemEntity.fromDto(dto.content),
        })
      case ExploreFilter.BOX:
        return new ExploreBoxContentEntity({
          id: dto.content.id,
          type: ExploreFilter.BOX,
          box: ExploreBoxItemEntity.fromDto(dto.content),
        })
      case ExploreFilter.PROFILE:
        return new ExploreProfileContentEntity({
          id: dto.content.id,
          type: ExploreFilter.PROFILE,
          profile: ExploreProfileItemEntity.fromDto(dto.content),
        })
    }
  }
}

export class ExploreProgramContentEntity extends ExploreContentEntity {
  program: ExploreProgramItemEntity

  constructor(exploreProgramContent: ExploreProgramContentEntity) {
    super(exploreProgramContent)
    this.program = exploreProgramContent.program
  }
}

export class ExploreProgramBundleContentEntity extends ExploreContentEntity {
  program: ExploreProgramBundleItemEntity

  constructor(exploreProgramContent: ExploreProgramBundleContentEntity) {
    super(exploreProgramContent)
    this.program = exploreProgramContent.program
  }
}

export class ExploreProProgramContentEntity extends ExploreContentEntity {
  program: ExploreProProgramItemEntity

  constructor(exploreProgramContent: ExploreProProgramContentEntity) {
    super(exploreProgramContent)
    this.program = exploreProgramContent.program
  }
}

export class ExploreBenchmarkContentEntity extends ExploreContentEntity {
  benchmark: ExploreBenchmarkItemEntity

  constructor(exploreBenchmarkContent: ExploreBenchmarkContentEntity) {
    super(exploreBenchmarkContent)
    this.benchmark = exploreBenchmarkContent.benchmark
  }
}
export class ExploreBoxContentEntity extends ExploreContentEntity {
  box: ExploreBoxItemEntity

  constructor(exploreBoxContent: ExploreBoxContentEntity) {
    super(exploreBoxContent)
    this.box = exploreBoxContent.box
  }
}
export class ExploreProfileContentEntity extends ExploreContentEntity {
  profile: ExploreProfileItemEntity

  constructor(exploreProfileContent: ExploreProfileContentEntity) {
    super(exploreProfileContent)
    this.profile = exploreProfileContent.profile
  }
}

export class ExploreBenchmarkItemEntity {
  id: string
  title: string
  category: string
  chrono: Chrono | null
  isFavorite: boolean
  rpe: number | null
  _type: ExploreFilter.BENCHMARK

  constructor(benchmarkItem: ExploreBenchmarkItemEntity) {
    this.id = benchmarkItem.id
    this._type = ExploreFilter.BENCHMARK
    this.title = benchmarkItem.title
    this.category = benchmarkItem.category
    this.chrono = benchmarkItem.chrono
    this.isFavorite = benchmarkItem.isFavorite
    this.rpe = benchmarkItem.rpe
  }

  static fromDto(dto: ExploreBenchmarkItemDto): ExploreBenchmarkItemEntity {
    return new ExploreBenchmarkItemEntity({
      id: dto.id,
      _type: ExploreFilter.BENCHMARK,
      title: dto.title,
      category: dto.category,
      chrono: dto.chrono ? convertChronoFromDto(dto.chrono) : null,
      isFavorite: dto.isFavorite,
      rpe: dto.rpe ?? null,
    })
  }
}

export class ExploreProfileItemEntity {
  id: string
  _type: ExploreFilter.PROFILE
  firstname: string
  lastname: string
  username: string
  avatar?: HUImage

  constructor(profileItem: ExploreProfileItemEntity) {
    this.id = profileItem.id
    this._type = ExploreFilter.PROFILE
    this.firstname = profileItem.firstname
    this.lastname = profileItem.lastname
    this.username = profileItem.username
    this.avatar = profileItem.avatar
  }

  static fromDto(dto: ExploreProfileItemDto): ExploreProfileItemEntity {
    return new ExploreProfileItemEntity({
      id: dto.id,
      _type: ExploreFilter.PROFILE,
      firstname: dto.firstname,
      lastname: dto.lastname,
      username: dto.username,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
    })
  }
}

export class ExploreBoxItemEntity {
  id: string
  _type: ExploreFilter.BOX
  name: string
  address: Address
  logo: HUImage
  cover: HUImage
  isSubscribed: boolean
  event?: boolean

  constructor(boxItem: ExploreBoxItemEntity) {
    this.id = boxItem.id
    this._type = ExploreFilter.BOX
    this.name = boxItem.name
    this.address = boxItem.address
    this.logo = boxItem.logo
    this.cover = boxItem.cover
    this.isSubscribed = boxItem.isSubscribed
    this.event = boxItem.event
  }

  static fromDto(dto: ExploreBoxItemDto): ExploreBoxItemEntity {
    return new ExploreBoxItemEntity({
      id: dto.id,
      _type: ExploreFilter.BOX,
      name: dto.name,
      logo: convertImageFromDto(dto.logo),
      cover: convertImageFromDto(dto.cover),
      address: dto.address,
      isSubscribed: dto.isSubscribed,
      event: dto.event ?? undefined,
    })
  }
}

export class ExploreProgramItemEntity {
  id: string
  name: string
  image?: HUImage
  owner: UserEntity | null
  visibility: ProgramVisibility
  subscribed: boolean
  subscriptionCount: number
  price: PriceObjectEntity | null
  defaultHideSessionContent: boolean
  _type: ExploreFilter.PROGRAM
  programType: ProgramType
  exploreType: ExploreFilter.PROGRAM

  constructor(programItem: ExploreProgramItemEntity) {
    this.id = programItem.id
    this.name = programItem.name
    this.image = programItem.image
    this.owner = programItem.owner
    this.visibility = programItem.visibility
    this.subscribed = programItem.subscribed
    this.subscriptionCount = programItem.subscriptionCount
    this.price = programItem.price
    this.defaultHideSessionContent = programItem.defaultHideSessionContent
    this.programType = programItem.programType
    this.exploreType = programItem.exploreType
    this._type = ExploreFilter.PROGRAM
  }

  static fromDto(dto: ExploreProgramItemDto): ExploreProgramItemEntity {
    return new ExploreProgramItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : undefined,
      owner: UserEntity.fromDto(dto.owner),
      visibility: dto.visibility,
      subscribed: dto.subscribed,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      programType: dto.type,
      exploreType: dto.exploreType,
      _type: ExploreFilter.PROGRAM,
    })
  }

  static fromProgram(program: UserProgramItemEntity | UserProgramDetailsEntity): ExploreProgramItemEntity {
    return new ExploreProgramItemEntity({
      id: program.id,
      name: program.name,
      image: program.image ?? undefined,
      owner: program.owner ?? null,
      visibility: program.visibility,
      subscribed: program.subscribed,
      subscriptionCount: program.subscriptionCount,
      price: program.price ?? null,
      defaultHideSessionContent: program.defaultHideSessionContent,
      programType: program.type,
      exploreType: ExploreFilter.PROGRAM,
      _type: ExploreFilter.PROGRAM,
    })
  }
}

export class ExploreProgramBundleItemEntity {
  id: string
  name: string
  image?: HUImage
  owner: UserEntity
  visibility: ProgramVisibility
  subscribed: boolean
  subscriptionCount: number
  price?: PriceObjectEntity
  nbPrograms: number
  _type: ExploreFilter.PROGRAM_BUNDLE
  programType: ProgramBundleType
  exploreType: ExploreFilter.PROGRAM_BUNDLE

  constructor(programItem: ExploreProgramBundleItemEntity) {
    this.id = programItem.id
    this.name = programItem.name
    this.image = programItem.image
    this.owner = programItem.owner
    this.visibility = programItem.visibility
    this.subscribed = programItem.subscribed
    this.subscriptionCount = programItem.subscriptionCount
    this.price = programItem.price
    this.nbPrograms = programItem.nbPrograms
    this.programType = programItem.programType
    this.exploreType = programItem.exploreType
    this._type = ExploreFilter.PROGRAM_BUNDLE
  }

  static fromDto(dto: ExploreProgramBundleItemDto): ExploreProgramBundleItemEntity {
    return new ExploreProgramBundleItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : undefined,
      owner: UserEntity.fromDto(dto.owner),
      visibility: dto.visibility,
      subscribed: dto.subscribed,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : undefined,
      programType: dto.type,
      exploreType: dto.exploreType,
      nbPrograms: dto.nbPrograms,
      _type: ExploreFilter.PROGRAM_BUNDLE,
    })
  }
}

export class ExploreProProgramItemEntity {
  id: string
  name: string
  image?: HUImage
  owner: UserEntity | null
  visibility: ProgramVisibility
  subscribed: boolean
  subscriptionCount: number
  price: PriceObjectEntity | null
  defaultHideSessionContent: boolean
  _type: ExploreFilter.PROGRAM_PRO
  programType: ProgramType
  exploreType: ExploreFilter.PROGRAM_PRO

  constructor(programItem: ExploreProProgramItemEntity) {
    this.id = programItem.id
    this.name = programItem.name
    this.image = programItem.image
    this.owner = programItem.owner
    this.visibility = programItem.visibility
    this.subscribed = programItem.subscribed
    this.subscriptionCount = programItem.subscriptionCount
    this.price = programItem.price
    this.defaultHideSessionContent = programItem.defaultHideSessionContent
    this.programType = programItem.programType
    this.exploreType = programItem.exploreType
    this._type = ExploreFilter.PROGRAM_PRO
  }

  static fromDto(dto: ExploreProProgramItemDto): ExploreProProgramItemEntity {
    return new ExploreProProgramItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : undefined,
      owner: UserEntity.fromDto(dto.owner),
      visibility: dto.visibility,
      subscribed: dto.subscribed,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      programType: dto.type,
      exploreType: dto.exploreType,
      _type: ExploreFilter.PROGRAM_PRO,
    })
  }

  static fromProgram(program: UserProgramItemEntity | UserProgramDetailsEntity): ExploreProProgramItemEntity {
    return new ExploreProProgramItemEntity({
      id: program.id,
      name: program.name,
      owner: program.owner ?? null,
      visibility: program.visibility,
      subscribed: program.subscribed,
      subscriptionCount: program.subscriptionCount,
      price: program.price ?? null,
      defaultHideSessionContent: program.defaultHideSessionContent,
      programType: program.type,
      exploreType: ExploreFilter.PROGRAM_PRO,
      _type: ExploreFilter.PROGRAM_PRO,
      image: program.image ?? undefined,
    })
  }
}
